import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import axios from 'axios';

import { Observable, Subject } from 'rxjs';
import { Page, Profile } from 'src/app/data-models/models';
import { environment } from 'src/environments/environment';

import { NavigatorService } from '../vendor/navigator.service';


export enum PlatformStates {
    PROFESSIONAL = 0,
    DEMO = 1
}

export enum PlatformViewStates {
    MOBILE = 0,
    DESKTOP = 1
}

export enum CompanyTiers {
    STARTER = "starter",
    GROWTH = "growth",
    PRO = "pro",
    TRIAL = "trial"
}


const AUTH_TOKEN_KEY = 'authToken';

declare function fbq(
    command: string,
    eventName: string,
    parameters?: Record<string, any>,
    options?: { eventID?: string }
): void;

interface Crypto {
    randomUUID: () => string;
}
  


@Injectable({
    providedIn: 'root'
})

export class PlatformService {

    private platformState: number = PlatformStates.DEMO;
    private viewState: number = PlatformViewStates.DESKTOP;
    private isAiChatOpen: boolean = false;

    private permissionsSubject: Subject<Array<Page>> = new Subject<Array<Page>>();

    private userPermissions: Array<Page> = [];
    public minutesUsed: number = 0;
    public trialMinutesRemaining: number = 0;

    // LIVE
    private companyTierPricing = {
        [CompanyTiers.TRIAL]: {
            title: "Starter",
            price: 0,
            aiCallingMinutes: 0.20,
            priceId: null,
            aiCallingMinutesId: null,
            platformServiceFeeId: null
        },
        [CompanyTiers.STARTER]: {
            title: "Starter",
            price: 99000,
            aiCallingMinutes: 0.20,
            priceId: "price_1RGx8E2NcbILvxexm3tGIi8B",
            aiCallingMinutesId: "price_1RIEUh2NcbILvxexeRDgdBTA",
            platformServiceFeeId: "price_1RIEbI2NcbILvxex8EPcpmtA"
        },
        [CompanyTiers.GROWTH]: {
            title: "Growth",
            price: 199000,
            aiCallingMinutes: 0.20,
            priceId: "price_1RGxA12NcbILvxexA0RHFqYg",
            aiCallingMinutesId: "price_1RIEUh2NcbILvxexeRDgdBTA",
            platformServiceFeeId: "price_1RIEbI2NcbILvxex8EPcpmtA"
        },
        [CompanyTiers.PRO]: {
            title: "Pro",
            price: 299000,
            aiCallingMinutes: 0.20,
            priceId: "price_1RGxAk2NcbILvxexbTlEfNZd",
            aiCallingMinutesId: "price_1RIEUh2NcbILvxexeRDgdBTA",
            platformServiceFeeId: "price_1RIEbI2NcbILvxex8EPcpmtA"
        }
    }

    // Development
    // private companyTierPricing = {
    //     [CompanyTiers.TRIAL]: {
    //         title: "Starter",
    //         price: 0,
    //         aiCallingMinutes: 0.20,
    //         priceId: null,
    //         aiCallingMinutesId: null,
    //         platformServiceFeeId: null
    //     },
    //     [CompanyTiers.STARTER]: {
    //         title: "Starter",
    //         price: 99000,
    //         aiCallingMinutes: 0.20,
    //         priceId: "price_1RIFHa2MJs2Hc9yDVTJ9m46M",
    //         aiCallingMinutesId: "price_1RIFIf2MJs2Hc9yDITGofWyp",
    //         platformServiceFeeId: "price_1RIFJv2MJs2Hc9yD1D1MGUG9"
    //     },
    //     [CompanyTiers.GROWTH]: {
    //         title: "Growth",
    //         price: 199000,
    //         aiCallingMinutes: 0.20,
    //         priceId: "price_1RIFHI2MJs2Hc9yDkSLAFXVE",
    //         aiCallingMinutesId: "price_1RIFIf2MJs2Hc9yDITGofWyp",
    //         platformServiceFeeId: "price_1RIFJv2MJs2Hc9yD1D1MGUG9"
    //     },
    //     [CompanyTiers.PRO]: {
    //         title: "Pro",
    //         price: 299000,
    //         aiCallingMinutes: 0.20,
    //         priceId: "price_1RIFGN2MJs2Hc9yDctFEOR08",
    //         aiCallingMinutesId: "price_1RIFIf2MJs2Hc9yDITGofWyp",
    //         platformServiceFeeId: "price_1RIFJv2MJs2Hc9yD1D1MGUG9"
    //     }
    // }


    constructor(
        private http: HttpClient, 
        private navigatorService: NavigatorService, 
        public router: Router) { 

    }

    public init() {
        this.initViewState();
    }

    private initViewState(): void {

        this.viewState = PlatformViewStates.DESKTOP;

    }

    public async getCallingMinutesUsed(startDate: Date, endDate: Date): Promise<number> {
        const token: string | null = this.getAuthToken();

        await this.http.post(`${environment.hostURL}/api/calling/getMinutesUsed`, { token: token, startDate: startDate, endDate: endDate } ).toPromise().then( 
            async (data: { message: string, usage: number }) => {
                console.log("Calling Minutes Used: ", data );

                this.minutesUsed = data?.usage;
                
                const trialMinutesRemaining = environment.trialMaxCallingMinutes - this.minutesUsed;
                this.trialMinutesRemaining = Math.max(0, Math.round(trialMinutesRemaining));
                
            }
        );

        return this.minutesUsed;
    }

    public getTiers() {
        return this.companyTierPricing;
    }

    public getPlatformViewState(): number {
        return this.viewState;
    }

    public getPlatformState(): number {
        return this.platformState;
    }

    public getAiChatState(): boolean {
        return this.isAiChatOpen;
    }

    public setAiChatState(state: boolean): boolean {
        return this.isAiChatOpen = state;
    }

    public observeUserPermissions(): Observable<Array<Page>> {
        return this.permissionsSubject.asObservable();
    }

    public getPermissions(): Array<Page> {
        return this.userPermissions;
    }

    public async refreshUserPermissions(): Promise<void> {
    
        const permissions: Array<Page> = await this.navigatorService.getAsyncEmployeePagesPermissions(this.navigatorService.getProfileId());
    
        if(!permissions) return

        console.log("Returned User Permissions: ", permissions);
        this.userPermissions = permissions;

        this.permissionsSubject.next(this.userPermissions);
    
    }

    public setPlatformState(state: number): void {
        this.platformState = state;

        if(this.platformState == PlatformStates.DEMO) {
            console.log("Platform State Demo");
        } else {
            console.log("Platform State Professional");
        }
        
    }

    public versionChecker(version: string): boolean {
        let isGreaterThan: boolean = false;
        let versionBreakdown: Array<string> = version.split(".");
        let currentVersionBreakdown: Array<string> = environment.version.split(".");

        let segmentIndex: number = 0;

        for(let versionSegment of versionBreakdown) {

            let versionFormattedSegment: number = Number(versionSegment);
            let currentVersionFormattedSegment: number = Number(currentVersionBreakdown[segmentIndex]);

            if(currentVersionFormattedSegment > versionFormattedSegment) {
                console.log("Version 2 Features Authorized");
                isGreaterThan = true;
                break;
            }

            else if (currentVersionFormattedSegment == versionFormattedSegment) {
                continue;
            }

            else {
                isGreaterThan = false;
                break;
            }

            segmentIndex++; 
        }

        return isGreaterThan;
    }

    public internalToolsChecker(): boolean {

        let internal: boolean = false;
        let companyId: string = this.navigatorService.getCompanyId();

        if(companyId == environment.adminCompanyId) {
            internal = true;
        }

        return internal;

    }

    public async metaPurchaseEventTracker(amount: number): Promise<void> {
        const eventId = (window.crypto as any).randomUUID();
        // const eventId = "TEST1534";

        const userAgent = navigator.userAgent;
        const sourceUrl = window.location.href;
        const profile: Profile = this.navigatorService.getCurrentProfile();
    
        fbq('track', 'Purchase', {
            value: amount,
            currency: 'USD'
        }, { eventID: eventId });
    
        await axios.post(`${environment.hostURL}/api/tracking/send-purchase-event`, {
            email: profile?.email ?? "noemail",
            phone: profile?.phone ?? "nophone",
            eventId,
            userAgent,
            sourceUrl,
            purchaseAmount: amount
        });
    }

    public async metaCompleteRegistrationEventTracker(): Promise<void> {
        const profile: Profile = this.navigatorService.getCurrentProfile();

        const eventId = (window.crypto as any).randomUUID();
        // const eventId = "TEST1534";

        const userAgent = navigator.userAgent;
        const sourceUrl = window.location.href;
    
        fbq('track', 'CompleteRegistration', {}, { eventID: eventId });
    
        await axios.post(`${environment.hostURL}/api/tracking/send-complete-registration-event`, {
            email: profile?.email ?? "noemail",
            phone: profile?.phone ?? "nophone",
            eventId,
            userAgent,
            sourceUrl
        });
    }

    public async metaStartTrialEventTracker(): Promise<void> {
        const profile: Profile = this.navigatorService.getCurrentProfile();

        const userAgent = navigator.userAgent;
        const sourceUrl = window.location.href;
    
        // No eventID unless you want to manually deduplicate this one too
        fbq('trackCustom', 'StartTrial');
    
        await axios.post(`${environment.hostURL}/api/tracking/send-start-trial-event`, {
            email: profile?.email ?? "noemail",
            phone: profile?.phone ?? "nophone",
            userAgent,
            sourceUrl
        });
    }    
    
    

    /**
     * Retrieves the authToken from local storage.
     * @returns {string | null} The authToken if it exists, otherwise null.
     */
    public getAuthToken(): string | null {
        return localStorage.getItem(AUTH_TOKEN_KEY);
    }
    
    /**
     * Sets the authToken in local storage.
     * @param token - The authToken to be saved.
     */
    public setAuthToken(token: string): void {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
    }

    /**
     * Deletes the authToken from local storage.
     */
    public deleteAuthToken(): void {
        localStorage.removeItem(AUTH_TOKEN_KEY);
    }

    private async addPasswordToProfile(email: string, password: string): Promise<string | null> {
        let token: string | null = null

        await this.http.post(`${environment.hostURL}/platform/api/upgradeProfile`, { email: email, password: password } ).toPromise().then( (data: { message: string, token: string | null, profile: Profile | null }) => {
            console.log("Profile Password Added", data );

            token = data?.token;

        });

        return token;
    }

    public async tokenLogin(): Promise<boolean> {
        let success: boolean = false;
        const token = this.getAuthToken();

        if (token) {
            await this.http.post(`${environment.hostURL}/platform/api/tokenLogin`, { token: token } ).toPromise().then( async (data: { message: string, token: string, profile: Profile | null }) => {
                console.log("Token Login", data );

                if(data?.token) {
                    this.setAuthToken(data?.token);
                
                    await this.navigatorService.login(data?.profile?.email, data?.profile?.password).then((isSuccess: boolean) => {

                        success = isSuccess;

                    });
                
                }

            });
        }

        return success;
    }

    private async hiveLogin(email: string, password: string): Promise<string | null> {
        let token: string | null = null

        await this.http.post(`${environment.hostURL}/platform/api/emailLogin`, { email: email, password: password } ).toPromise().then( (data: { message: string, success: boolean, token: string | null }) => {
            console.log("Hive Login", data );

            token = data?.token;

        });

        return token;
    }

    public async emailLogin(email: string, password: string): Promise<boolean> {
        let success: boolean = false;
    
        // Mongo Realm Login - Deprecated
        await this.navigatorService.login(email, password).then(async (isSuccess: boolean) => {

            success = isSuccess;

            if (success) {

                const profile: Profile = this.navigatorService.getCurrentProfile();
                let token: string | null = null;
                let self = this;

                // Swarm BE Login
                setTimeout(async () => {

                    if (!profile?.version || profile?.version < 2 ) {
                        token = await self.addPasswordToProfile(email, password);
                    } else {
                        token = await self.hiveLogin(email, password);
                    }
    
                    if (token) {
                        self.setAuthToken(token);
                    }

                }, 0);


            }

        });

        return success;
    }

    public async logout(): Promise<void> {

        this.deleteAuthToken();
        this.navigatorService.logout();

    }

}